/**
 * MemberNotificationsTab - Inline notification preferences + recent notification
 * history for a member. Embedded in the MemberProfileNew tabs.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Bell,
  BellOff,
  Shield,
  Smartphone,
  Mail,
  MessageSquare,
  Moon,
  Save,
  AlertCircle,
  Clock,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { EscalationTierExplainer } from './EscalationTierExplainer';
import { useMemberNotificationPreferences, useSnoozes } from '@/hooks/useEcmReminders';
import type { MemberNotificationPreferencesUpdate } from '@/services/api/ecmReminders';
import { apiClient } from '@/services/apiClient';

interface MemberNotificationsTabProps {
  memberId: number;
  memberName: string;
}

const ESCALATION_OPTIONS: { value: number | null; label: string; desc: string }[] = [
  { value: null, label: 'Default', desc: 'Use program setting' },
  { value: 1, label: 'Tier 1', desc: 'Initial only' },
  { value: 2, label: 'Tier 2', desc: '+ Follow-up' },
  { value: 3, label: 'Tier 3', desc: '+ Urgent' },
  { value: 4, label: 'Tier 4', desc: '+ Supervisor' },
  { value: 0, label: 'Off', desc: 'No escalation' },
];

const TIMEZONE_OPTIONS = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
];

interface RecentNotification {
  id: number;
  notification_type: string;
  title: string;
  message: string;
  priority: string;
  read: boolean;
  created_at: string;
}

export const MemberNotificationsTab: React.FC<MemberNotificationsTabProps> = ({
  memberId,
  memberName,
}) => {
  const { preferences, loading, saving, updatePreferences, refetch } =
    useMemberNotificationPreferences(memberId);
  const { snoozes } = useSnoozes(memberId);

  // Local form state
  const [paused, setPaused] = useState(false);
  const [pausedReason, setPausedReason] = useState('');
  const [escalationTier, setEscalationTier] = useState<number | null>(null);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [inAppEnabled, setInAppEnabled] = useState(true);
  const [maxPerDay, setMaxPerDay] = useState<number>(3);
  const [maxPerWeek, setMaxPerWeek] = useState<number>(10);
  const [quietStart, setQuietStart] = useState('21:00');
  const [quietEnd, setQuietEnd] = useState('08:00');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [dirty, setDirty] = useState(false);

  // Recent notifications for this member
  const [recentNotifications, setRecentNotifications] = useState<RecentNotification[]>([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);

  // Sync form with fetched preferences
  useEffect(() => {
    if (preferences) {
      setPaused(preferences.notification_paused);
      setPausedReason(preferences.paused_reason || '');
      setEscalationTier(preferences.max_escalation_tier);
      setSmsEnabled(preferences.sms_enabled);
      setEmailEnabled(preferences.email_enabled);
      setInAppEnabled(preferences.in_app_enabled);
      setMaxPerDay(preferences.max_reminders_per_day ?? 3);
      setMaxPerWeek(preferences.max_reminders_per_week ?? 10);
      setQuietStart(preferences.quiet_start || '21:00');
      setQuietEnd(preferences.quiet_end || '08:00');
      setTimezone(preferences.timezone || 'America/Los_Angeles');
      setDirty(false);
    }
  }, [preferences]);

  // Load recent notifications for this member
  useEffect(() => {
    const loadNotifications = async () => {
      setLoadingNotifs(true);
      try {
        const response = await apiClient.get('/api/notifications', {
          params: {
            notification_type: 'enrollment',
            page_size: 10,
          },
        });
        // Filter to this member if the response contains related_member_id
        const notifs = (response.data?.notifications || response.data || []) as RecentNotification[];
        setRecentNotifications(notifs.slice(0, 10));
      } catch {
        // Notifications may not be available — that's fine
      } finally {
        setLoadingNotifs(false);
      }
    };
    loadNotifications();
  }, [memberId]);

  const markDirty = () => setDirty(true);

  const handleSave = async () => {
    const data: MemberNotificationPreferencesUpdate = {
      notification_paused: paused,
      paused_reason: paused ? (pausedReason || 'Manual pause') : undefined,
      max_escalation_tier: escalationTier ?? undefined,
      sms_enabled: smsEnabled,
      email_enabled: emailEnabled,
      in_app_enabled: inAppEnabled,
      max_reminders_per_day: maxPerDay,
      max_reminders_per_week: maxPerWeek,
      quiet_start: quietStart,
      quiet_end: quietEnd,
      timezone,
    };

    const result = await updatePreferences(data);
    if (result) {
      toast.success('Notification preferences saved');
      setDirty(false);
    } else {
      toast.error('Failed to save preferences');
    }
  };

  const handleOptOut = async () => {
    const result = await updatePreferences({ opted_out: true });
    if (result) {
      toast.success(`${memberName} opted out of all notifications`);
      refetch();
    }
  };

  const handleReEnable = async () => {
    const result = await updatePreferences({ opted_out: false });
    if (result) {
      toast.success(`${memberName} re-enabled for notifications`);
      refetch();
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-sm text-gray-400">Loading notification preferences...</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Opted-out banner */}
      {preferences?.opted_out && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BellOff className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">
                    Notifications Opted Out
                  </p>
                  <p className="text-xs text-red-500 dark:text-red-400">
                    All notifications and reminders are disabled for this member.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-100"
                onClick={handleReEnable}
                disabled={saving}
              >
                Re-enable Notifications
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active snooze banner */}
      {snoozes.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Reminders snoozed until{' '}
                <span className="font-medium">
                  {new Date(snoozes[0].snooze_until).toLocaleDateString()}
                </span>
                {snoozes[0].reason && (
                  <span className="text-amber-500"> ({snoozes[0].reason})</span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Preferences form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Pause toggle */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Bell className="w-4 h-4 text-blue-600" />
                Notification Controls
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 px-4 pb-4">
              {/* Pause toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Pause All Notifications</p>
                  <p className="text-xs text-gray-500">Temporarily stop all reminders and escalations</p>
                </div>
                <Switch
                  checked={paused}
                  onCheckedChange={(v) => { setPaused(v); markDirty(); }}
                  disabled={preferences?.opted_out}
                />
              </div>
              {paused && (
                <input
                  type="text"
                  value={pausedReason}
                  onChange={(e) => { setPausedReason(e.target.value); markDirty(); }}
                  placeholder="Reason for pausing..."
                  className="w-full px-3 py-1.5 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                  maxLength={100}
                />
              )}

              {/* Escalation cap */}
              <div className="space-y-2 pt-2 border-t dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Escalation Level</p>
                </div>
                <div className="flex gap-1">
                  {ESCALATION_OPTIONS.map((opt) => {
                    const isSelected =
                      (opt.value === null && escalationTier === null) ||
                      opt.value === escalationTier;
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={() => { setEscalationTier(opt.value); markDirty(); }}
                        disabled={preferences?.opted_out}
                        className={`flex-1 px-1 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                          isSelected
                            ? opt.value === 0
                              ? 'border-red-400 bg-red-50 text-red-700'
                              : 'border-blue-400 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40'
                        }`}
                        title={opt.desc}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <EscalationTierExplainer memberCap={escalationTier} />
              </div>

              {/* Channel toggles */}
              <div className="space-y-2 pt-2 border-t dark:border-gray-700">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Channels</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="flex items-center justify-between p-2 border rounded-md">
                    <div className="flex items-center gap-1.5">
                      <Smartphone className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-600">SMS</span>
                    </div>
                    <Switch
                      checked={smsEnabled}
                      onCheckedChange={(v) => { setSmsEnabled(v); markDirty(); }}
                      disabled={preferences?.opted_out || paused}
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 border rounded-md">
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-600">Email</span>
                    </div>
                    <Switch
                      checked={emailEnabled}
                      onCheckedChange={(v) => { setEmailEnabled(v); markDirty(); }}
                      disabled={preferences?.opted_out || paused}
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 border rounded-md">
                    <div className="flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-600">In-App</span>
                    </div>
                    <Switch
                      checked={inAppEnabled}
                      onCheckedChange={(v) => { setInAppEnabled(v); markDirty(); }}
                      disabled={preferences?.opted_out || paused}
                    />
                  </div>
                </div>
              </div>

              {/* Frequency + Quiet hours */}
              <div className="grid grid-cols-2 gap-4 pt-2 border-t dark:border-gray-700">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Frequency Caps</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Max/day</span>
                      <input
                        type="number"
                        value={maxPerDay}
                        onChange={(e) => { setMaxPerDay(Number(e.target.value)); markDirty(); }}
                        min={1} max={50}
                        disabled={preferences?.opted_out || paused}
                        className="w-16 px-2 py-1 border rounded text-xs text-right bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 disabled:opacity-40"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Max/week</span>
                      <input
                        type="number"
                        value={maxPerWeek}
                        onChange={(e) => { setMaxPerWeek(Number(e.target.value)); markDirty(); }}
                        min={1} max={200}
                        disabled={preferences?.opted_out || paused}
                        className="w-16 px-2 py-1 border rounded text-xs text-right bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 disabled:opacity-40"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Moon className="w-3.5 h-3.5 text-gray-500" />
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Quiet Hours</p>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="time" value={quietStart}
                      onChange={(e) => { setQuietStart(e.target.value); markDirty(); }}
                      disabled={preferences?.opted_out || paused}
                      className="flex-1 px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 disabled:opacity-40"
                    />
                    <span className="text-xs text-gray-400 self-center">to</span>
                    <input
                      type="time" value={quietEnd}
                      onChange={(e) => { setQuietEnd(e.target.value); markDirty(); }}
                      disabled={preferences?.opted_out || paused}
                      className="flex-1 px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 disabled:opacity-40"
                    />
                  </div>
                  <select
                    value={timezone}
                    onChange={(e) => { setTimezone(e.target.value); markDirty(); }}
                    disabled={preferences?.opted_out || paused}
                    className="w-full px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 disabled:opacity-40"
                  >
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz.replace('America/', '').replace('Pacific/', '').replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Save button */}
              <div className="flex items-center justify-between pt-3 border-t dark:border-gray-700">
                {!preferences?.opted_out && (
                  <button
                    type="button"
                    onClick={handleOptOut}
                    className="text-xs text-red-500 hover:text-red-700"
                    disabled={saving}
                  >
                    Opt out of all notifications
                  </button>
                )}
                <div className="ml-auto">
                  <Button
                    onClick={handleSave}
                    disabled={saving || !dirty || preferences?.opted_out}
                    size="sm"
                  >
                    <Save className="w-3.5 h-3.5 mr-1" />
                    {saving ? 'Saving...' : 'Save Preferences'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Recent notifications */}
        <div>
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-medium">Recent Notifications</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {loadingNotifs ? (
                <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
              ) : recentNotifications.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No recent notifications</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {recentNotifications.map((n) => (
                    <div
                      key={n.id}
                      className={`p-2 rounded border text-xs ${
                        n.read
                          ? 'bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800'
                          : 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-800'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-gray-800 dark:text-gray-200 line-clamp-2">
                          {n.title}
                        </p>
                        <Badge
                          variant="outline"
                          className={`text-[9px] flex-shrink-0 ${
                            n.priority === 'urgent'
                              ? 'text-red-600 border-red-200'
                              : n.priority === 'high'
                              ? 'text-orange-600 border-orange-200'
                              : 'text-gray-500 border-gray-200'
                          }`}
                        >
                          {n.priority}
                        </Badge>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                        {new Date(n.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
